import './styles/App.scss';
import './functions';
import { useState, useEffect } from 'react';
import Calendar from './components/Calendar';
import Sidebar from './components/Sidebar';
import Login from './components/Login';
import Charts from './components/Charts';
import { db, auth, signOutUser } from './firebase';
import { collection, getDocs, query, where, updateDoc, doc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';


function App() {

  const getGlobalData = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'global-data'));
      const items = [];
      querySnapshot.forEach((doc) => {
        items.push({ id: "NuOD6JPMGcMqaUxuhCqO", ...doc.data() });
      });
      return items;
    } catch (error) {
      console.error("Error fetching global data: ", error);
      return [];
    }
  };
  
  // Fetch data for a specific year, month, and optionally day
  const fetchDataByDate = async (year, month, day = null) => {
    try {
      const constraints = [
        where('activity_date_year', '==', year.toString()),
        where('activity_date_month', '==', month.toString())
      ];
      
      if (day !== null) {
        constraints.push(where('activity_date_day', '==', day.toString()));
      }
      
      const q = query(collection(db, 'activity-data'), ...constraints);
      const querySnapshot = await getDocs(q);
      const items = [];
      querySnapshot.forEach((doc) => {
        items.push({ id: doc.id, ...doc.data() });
      });
      return items;
    } catch (error) {
      console.error("Error fetching data by date: ", error);
      return [];
    }
  };

  // Set data for a specific year, month, and optionally day
  const setDataByDate = async (year, month, day = null) => {
    try {
      let q = query(
        collection(db, 'activity-data'),
        where('activity_date_year', '==', year.toString()),
        where('activity_date_month', '==', month.toString())
      );

      if (month === null) {
        q = query(
          collection(db, 'activity-data'),
          where('activity_date_year', '==', year.toString())
        );
      }

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log("No document found for the specified date");
        return;
      }
      
      const updatedData = await fetchDataByDate(year, month);
      setData(updatedData);
    } catch (error) {
      console.error("Error setting data by date: ", error);
    }
  };

  // Update database entry for a specific date
  const updateDatabaseByDate = async (data, year, month, day) => {
    try {
      const q = query(
        collection(db, 'activity-data'),
        where('activity_date_year', '==', year.toString()),
        where('activity_date_month', '==', month.toString()),
        where('activity_date_day', '==', day.toString())
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log("No document found for the specified date");
        return;
      }
      
      querySnapshot.forEach(async (docSnapshot) => {
        await updateDoc(doc(db, 'activity-data', docSnapshot.id), {
          activity_steps: data.activity_steps || 0,
          activity_bike: data.activity_bike || 0,
          activity_weight: data.activity_weight || 0,
          activity_strength: data.activity_strength || false,
          activity_alcohol: data.activity_alcohol || false,
          activity_notes: data.activity_notes || '',
          activity_pressups: data.activity_pressups || 0
        });
      });
      
      // Refresh the calendar data after update
      const updatedData = await fetchDataByDate(year, month);
      setData(updatedData);
    } catch (error) {
      console.error("Error updating database: ", error);
    }
  };

  const [data, setData] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [globalData, setGlobalData] = useState([]);
  
  // Listen to authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Fetch data when user is logged in
  useEffect(() => {
    const fetchData = async () => {
      if (user) {
        var currentDate = new Date();
        var currentYear = currentDate.getFullYear();
        var currentMonth = currentDate.getMonth() + 1;
        var thisMonthsData = await fetchDataByDate(currentYear, currentMonth);
        setData(thisMonthsData);
        const globalDataItems = await getGlobalData();
        setGlobalData(globalDataItems);
      }
    };
    fetchData();
  }, [user]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Login onLoginSuccess={setUser} />;
  }

  const handleLogout = async () => {
    try {
      await signOutUser();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="App">
      <Fab color="primary" aria-label="add" className="add-activity-button" onClick={() => document.querySelector('.App-sidebar').classList.toggle('open')}>
        <AddIcon />
      </Fab>
      <header className="App-header">
        <div className="App-header-left">
          <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 45.8096C19.6865 45.8096 15.4698 44.5305 11.8832 42.134C8.29667 39.7376 5.50128 36.3314 3.85056 32.3462C2.19985 28.361 1.76794 23.9758 2.60947 19.7452C3.451 15.5145 5.52816 11.6284 8.57829 8.5783C11.6284 5.52817 15.5145 3.45101 19.7452 2.60948C23.9758 1.76795 28.361 2.19986 32.3462 3.85057C36.3314 5.50129 39.7376 8.29668 42.134 11.8833C44.5305 15.4698 45.8096 19.6865 45.8096 24L24 24L24 45.8096Z" fill="currentColor"></path>
          </svg>
          <p>APE68 LifeApp</p>
        </div>
        <div className="App-header-right">
          <span className="material-symbols-outlined">notifications</span>
          <button onClick={handleLogout} className="logout-button" title="Sign out">
            <LogoutIcon />
          </button>
        </div>
      </header>
      
      {data.length > 0 && <Sidebar data={data} fetchDataByDate={fetchDataByDate} updateDatabaseByDate={updateDatabaseByDate} />}
      <main className="App-main">
        {data.length > 0 && <Calendar data={data} globalData={globalData} fetchDataByDate={fetchDataByDate} setDataByDate={setDataByDate} />}
      </main>
      <footer className="App-footer">
        <p>© {new Date().getFullYear()} APE68. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;
